import { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Share2, Link2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { SiteSettings } from '../../types';
import { Spinner, Alert, inputClass, labelClass, primaryBtn, secondaryBtn } from '../../components/ui';

interface ContactFields {
  phone: string;
  email: string;
  address: string;
}

interface PartnerFields {
  buy_power_url: string;
  lobotech_url: string;
}

interface SocialFields {
  whatsapp: string;
  facebook: string;
  instagram: string;
  linkedin: string;
  tiktok: string;
}

const CONTACT_FIELDS: { key: keyof ContactFields; label: string; placeholder: string }[] = [
  { key: 'phone', label: 'Phone', placeholder: '+234 ...' },
  { key: 'email', label: 'Email Address', placeholder: 'info@...' },
  { key: 'address', label: 'Office Address', placeholder: 'No 3 Olumegbon Road, Ikoyi' },
];

const PARTNER_FIELDS: { key: keyof PartnerFields; label: string; placeholder: string }[] = [
  { key: 'buy_power_url', label: 'Buy Power Link', placeholder: 'https://wa.me/...' },
  { key: 'lobotech_url', label: 'Lobotech Link', placeholder: 'https://lobotech.com/...' },
];

const SOCIAL_FIELDS: { key: keyof SocialFields; label: string; placeholder: string }[] = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/...' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/...' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/...' },
];

export default function Contact() {
  const [contact, setContact] = useState<ContactFields>({ phone: '', email: '', address: '' });
  const [partner, setPartner] = useState<PartnerFields>({ buy_power_url: '', lobotech_url: '' });
  const [social, setSocial] = useState<SocialFields>({ whatsapp: '', facebook: '', instagram: '', linkedin: '', tiktok: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<SiteSettings>('/settings/private')
      .then((s) => {
        const rec = s as Record<string, string>;
        setContact({
          phone: rec.phone || '',
          email: rec.email || '',
          address: rec.address || '',
        });
        setPartner({
          buy_power_url: rec.buy_power_url || '',
          lobotech_url: rec.lobotech_url || '',
        });
        setSocial({
          whatsapp: rec.whatsapp || '',
          facebook: rec.facebook || '',
          instagram: rec.instagram || '',
          linkedin: rec.linkedin || '',
          tiktok: rec.tiktok || '',
        });
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const save = async (payload: Partial<ContactFields> | Partial<PartnerFields> | Partial<SocialFields>) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await api.put('/settings', payload);
      setMessage('Contact, partner & social settings saved. The public site will update immediately.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner label="Loading settings..." />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Contact & Social Media</h1>
      <p className="text-sm text-gray-500 -mt-4">
        These values control the contact information, partner links and social media shown across the public website (footer, navbar, contact page).
      </p>
      {message && <div className="px-4 py-3 rounded-lg bg-green-50 text-green-800 text-sm">{message}</div>}
      {error && <Alert>{error}</Alert>}

      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl">
        <div className="flex items-center mb-1">
          <Phone className="h-5 w-5 text-gray-500 mr-2" />
          <Mail className="h-5 w-5 text-gray-400 mr-2" />
          <MapPin className="h-5 w-5 text-gray-500" />
          <h2 className="font-semibold ml-2">Contact Information</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Phone, email and address shown on the public site.</p>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save(contact);
          }}
        >
          {CONTACT_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className={labelClass}>{label}</label>
              <input
                className={inputClass}
                value={contact[key]}
                onChange={(e) => setContact((c) => ({ ...c, [key]: e.target.value }))}
                placeholder={placeholder}
              />
            </div>
          ))}
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className={primaryBtn}>
              {saving ? 'Saving...' : 'Save Contact Info'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl">
        <div className="flex items-center mb-1">
          <Link2 className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="font-semibold">Partners</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Links shown in the "Partners" menu in the navbar. Leave blank to hide a partner link.
        </p>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save(partner);
          }}
        >
          {PARTNER_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className={labelClass}>{label}</label>
              <input
                className={inputClass}
                value={partner[key]}
                onChange={(e) => setPartner((p) => ({ ...p, [key]: e.target.value }))}
                placeholder={placeholder}
              />
            </div>
          ))}
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className={secondaryBtn}>
              {saving ? 'Saving...' : 'Save Partner Links'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl">
        <div className="flex items-center mb-1">
          <Share2 className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="font-semibold">Social Media Pages</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Links to your pages. Leave blank to hide that icon on the site.</p>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save(social);
          }}
        >
          {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className={labelClass}>{label}</label>
              <input
                className={inputClass}
                value={social[key]}
                onChange={(e) => setSocial((s) => ({ ...s, [key]: e.target.value }))}
                placeholder={placeholder}
              />
            </div>
          ))}
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className={secondaryBtn}>
              {saving ? 'Saving...' : 'Save Social Links'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
